import * as React from 'react';

import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type DropdownMenuProps } from '@radix-ui/react-dropdown-menu';
import { defineMessages, useIntl } from 'react-intl';

import { Button } from 'src/registry/ui/Button';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from 'src/registry/ui/DropdownMenu';

const messages = defineMessages({
    noOptions: {
        id: 'no-options',
        defaultMessage: 'No options found',
    },
    selectAll: {
        id: 'select-all',
        defaultMessage: 'Select all',
    },
    deselectAll: {
        id: 'deselect-all',
        defaultMessage: 'Deselect all',
    },
});

interface ContextProps {
    selected: string[];
    setSelected: (selected: string[]) => void;
}

const MultiSelectContext = React.createContext<ContextProps | null>(null);

interface MultiSelectProps extends DropdownMenuProps {
    value: string[];
    onChange: (value: string[]) => void;
}
const MultiSelect = ({ children, value, onChange, ...props }: MultiSelectProps) => {
    return (
        <MultiSelectContext.Provider value={{ selected: value, setSelected: onChange }}>
            <DropdownMenu {...props}>{children}</DropdownMenu>
        </MultiSelectContext.Provider>
    );
};

const MultiSelectTrigger = DropdownMenuTrigger;

interface MultiSelectTriggerProps extends React.ComponentProps<typeof Button> {
    fieldData?: any[];
}
// Used as a preconfigured component that shows selected options as badges
const MultiSelectControl = React.forwardRef<
    React.ComponentRef<typeof Button>,
    React.ComponentPropsWithoutRef<typeof Button & MultiSelectTriggerProps>
>(({ children, ...props }, ref) => {
    return (
        <Button variant="dropdown" ref={ref} {...props}>
            <FontAwesomeIcon icon={faChevronDown} className="text-primary-700 mr-1 h-3 w-4 shrink-0" />
            <span className="truncate">{children}</span>
        </Button>
    );
});
MultiSelectControl.displayName = 'MultiSelectControl';

const MultiSelectContent = React.forwardRef<
    React.ComponentRef<typeof DropdownMenuContent>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuContent> & {
        allValues?: string[];
    }
>(({ children, allValues, ...props }, ref) => {
    const { formatMessage } = useIntl();
    return (
        <DropdownMenuContent
            className="overflow-auto"
            style={{
                width: 'var(--radix-dropdown-menu-trigger-width)',
                maxHeight: 'var(--radix-dropdown-menu-content-available-height)',
            }}
            collisionPadding={20}
            align="start"
            ref={ref}
            {...props}
        >
            {!!children && (
                <>
                    {!!allValues && (
                        <>
                            <MultiSelectAllOption values={allValues} />
                            <DropdownMenuSeparator />
                        </>
                    )}
                    {children}
                </>
            )}
            {!children && (
                <div className="flex items-center justify-center p-4 text-sm text-gray-500">
                    {formatMessage(messages.noOptions)}
                </div>
            )}
        </DropdownMenuContent>
    );
});
MultiSelectContent.displayName = 'MultiSelectContent';

const MultiSelectOption = React.forwardRef<
    React.ComponentRef<typeof DropdownMenuCheckboxItem>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuCheckboxItem> & { selected: string }
>(({ children, ...props }, ref) => {
    const { selected, setSelected } = React.useContext(MultiSelectContext)!;

    const isChecked = selected.includes(props.selected);

    const onCheckedChange = () => {
        if (isChecked) {
            setSelected(selected.filter((v) => v !== props.selected));
        } else {
            setSelected([...selected, props.selected]);
        }
    };
    return (
        <DropdownMenuCheckboxItem
            onSelect={(e) => e.preventDefault()}
            checked={isChecked}
            onCheckedChange={onCheckedChange}
            ref={ref}
            {...props}
        >
            {children}
        </DropdownMenuCheckboxItem>
    );
});
MultiSelectOption.displayName = 'MultiSelectOption';

const MultiSelectAllOption = React.forwardRef<
    React.ComponentRef<typeof DropdownMenuCheckboxItem>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuCheckboxItem> & {
        values?: string[];
    }
>(({ children, values = [], ...props }, ref) => {
    const { selected, setSelected } = React.useContext(MultiSelectContext)!;
    const { formatMessage } = useIntl();

    const isChecked = selected.length === values.length && values.every((v) => selected.includes(v));

    const onSelect = (e: Event) => {
        e.preventDefault();
        if (isChecked) {
            setSelected([]);
        } else {
            setSelected(values);
        }
    };

    return (
        <DropdownMenuCheckboxItem onSelect={onSelect} checked={isChecked} ref={ref} {...props}>
            {formatMessage((children ?? !isChecked) ? messages.selectAll : messages.deselectAll)}
        </DropdownMenuCheckboxItem>
    );
});
MultiSelectAllOption.displayName = 'MultiSelectAllOption';

export {
    MultiSelect,
    MultiSelectTrigger,
    MultiSelectControl,
    MultiSelectContent,
    MultiSelectOption,
    MultiSelectAllOption,
};
